import axios from 'axios';
import { FINAL_API_BASE_URL } from '../config/config';

const LEGACY_TOKEN_KEY = 'sultan_access_token';

/**
 * Single Axios client — auth is JWT in httpOnly cookies (withCredentials).
 * No access token in JS storage / Authorization header.
 */
export const apiClient = axios.create({
  baseURL: FINAL_API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Clear any pre-refactor sessionStorage token. */
export function clearLegacyAccessToken() {
  try {
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

apiClient.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
    }
  }
  return config;
});

let refreshPromise = null;

async function handleUnauthorized(error) {
  const original = error.config;
  const url = original?.url || '';
  const isAuthEndpoint =
    url.includes('/auth/me') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/login');

  if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
    original._retry = true;
    try {
      // Refresh rotates access_token cookie; retry uses cookies automatically
      refreshPromise = refreshPromise || apiClient.post('/auth/refresh');
      await refreshPromise;
      refreshPromise = null;
      return apiClient(original);
    } catch (refreshErr) {
      refreshPromise = null;
      if (typeof handleUnauthorized.onRefreshFail === 'function') {
        handleUnauthorized.onRefreshFail();
      }
      return Promise.reject(refreshErr);
    }
  }
  return Promise.reject(error);
}

/** Attach 401 → cookie refresh → retry. Call once from AuthProvider. */
export function setupAuthInterceptors({ onRefreshFail } = {}) {
  handleUnauthorized.onRefreshFail = onRefreshFail;

  const id = apiClient.interceptors.response.use(
    (res) => res,
    (err) => handleUnauthorized(err)
  );

  return () => {
    apiClient.interceptors.response.eject(id);
  };
}

/**
 * Unwrap standard API envelope { success, message, data }.
 * Leaves blobs / non-envelope bodies unchanged.
 */
export function unwrapApi(body) {
  if (
    body &&
    typeof body === 'object' &&
    typeof body.success === 'boolean' &&
    Object.prototype.hasOwnProperty.call(body, 'data') &&
    !(typeof Blob !== 'undefined' && body instanceof Blob)
  ) {
    return body.data;
  }
  return body;
}

export function getApiErrorMessage(error, fallback = 'Request failed') {
  const body = error?.response?.data;
  if (body?.message) return body.message;
  const validationMsg = body?.data?.errors?.[0]?.msg;
  if (validationMsg) return validationMsg;
  return fallback;
}

export default apiClient;
